import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import styles from './AccountMenu.module.css';

export interface AccountMenuItem {
  label: string;
  onSelect: () => void;
}

export interface AccountMenuProps {
  /** The signed-in email, drawn on the trigger and at the top of the menu. */
  email: string;
  /** Account actions, in order. Sign out is always last. */
  items?: readonly AccountMenuItem[];
  onSignOut: () => void;
}

function initialsOf(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : local.slice(0, 2);
  return letters.toUpperCase();
}

/** The signed-in person top-right, opening a menu of account actions. */
export function AccountMenu({ email, items = [], onSignOut }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  function choose(action: () => void) {
    setOpen(false);
    action();
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const entries = Array.from(
      menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    const index = entries.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      entries[(index + step + entries.length) % entries.length]?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  }

  return (
    <div className={styles.root} ref={root}>
      <button
        ref={trigger}
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        className={styles.trigger}
        onClick={() => setOpen(!open)}
        type="button"
      >
        <span aria-hidden="true" className={styles.avatar}>
          {initialsOf(email)}
        </span>
        <span className={styles.name}>{email}</span>
        <span aria-hidden="true" className={styles.caret}>
          ▾
        </span>
      </button>
      {open ? (
        <div
          ref={menu}
          aria-label="Account"
          className={styles.menu}
          id={menuId}
          onKeyDown={onMenuKeyDown}
          role="menu"
        >
          <div className={styles.header} role="presentation">
            <span className={styles.headerName}>Signed in as</span>
            <span className={styles.headerEmail} title={email}>
              {email}
            </span>
          </div>
          {[...items, { label: 'Sign out', onSelect: onSignOut }].map((item) => (
            <button
              className={styles.item}
              key={item.label}
              onClick={() => choose(item.onSelect)}
              role="menuitem"
              tabIndex={-1}
              type="button"
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
